// Shared geometry, focus and layering for the ui primitives, so every
// control agrees on height, corner, focus ring and stacking order in every
// theme. Themes change colour, type family, radius values and material
// only — the numbers here are the same everywhere.
//
// Tailwind only generates classes it can see written out in full, so
// these are literal class strings (never build them from pieces at
// runtime).

import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

// tailwind-merge that also knows the semantic radius names, so a caller's
// `rounded-full` replaces a primitive's `rounded-control` instead of both
// classes shipping (and the later one in the stylesheet winning).
// lib/utils `cn` should adopt the same config; until then primitives use
// this one for their own className merging.
const merge = extendTailwindMerge({
  extend: {
    classGroups: {
      rounded: [{ rounded: ["control", "card", "pill"] }],
      // the named theme shadows in tailwind.config (not shadow colours)
      shadow: [{ shadow: ["card-panel", "card-tile", "lift-panel", "lift-tile"] }],
    },
  },
});

/** `cn` for the primitives: clsx + a radius-aware tailwind-merge. */
export function cx(...inputs: ClassValue[]): string {
  return merge(clsx(inputs));
}

/** Control heights in px (never rem), so they do not move with the root size. */
export const CONTROL_H = {
  sm: "h-[28px]",
  md: "h-[32px]",
  lg: "h-[40px]",
} as const;

/** Buttons, inputs, selects, rows and menu items. */
export const CONTROL_RADIUS = "rounded-control";

/**
 * Button corner: the control radius, unless a theme sets `--r-button`
 * (a hook for the theme system — e.g. pill buttons in one theme — that no
 * theme defines yet). tailwind-merge sees it as a radius class, so a
 * caller's `rounded-*` or `shape="pill"` still replaces it.
 */
export const BUTTON_RADIUS = "rounded-[var(--r-button,var(--r-control))]";

/**
 * Keyboard focus ring (the focus token, 2px, offset). `:focus-visible`
 * only, so a mouse click never draws it. index.css carries the same ring
 * globally; primitives state it so it survives a caller's outline class.
 */
export const FOCUS_RING =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus";

/** The same ring drawn inside the box, for items in clipped containers (tabs, menus, lists). */
export const FOCUS_RING_INSET =
  "focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus";

/**
 * 44px minimum on touch screens (pointer: coarse) — the companion hosts
 * desktop pages at phone width. Desktop geometry is unchanged.
 */
export const COARSE_TOUCH = "[@media(pointer:coarse)]:min-h-[44px]";

/**
 * Invisible 44px hit area on touch screens without changing the drawn
 * size: a centred ::after at least 44×44. Needs a positioned element
 * (Button is `relative`).
 */
export const COARSE_HIT_AREA =
  "[@media(pointer:coarse)]:after:absolute [@media(pointer:coarse)]:after:left-1/2 [@media(pointer:coarse)]:after:top-1/2 [@media(pointer:coarse)]:after:h-[max(100%,44px)] [@media(pointer:coarse)]:after:w-[max(100%,44px)] [@media(pointer:coarse)]:after:-translate-x-1/2 [@media(pointer:coarse)]:after:-translate-y-1/2 [@media(pointer:coarse)]:after:content-['']";

/**
 * Hover only where a real pointer hovers. A tap on a touch screen fires
 * :hover and it sticks until the next tap elsewhere, so hover surfaces
 * are written with this variant prefix, e.g.
 * `[@media(hover:hover)]:hover:bg-raised` (spelled out in full — see the
 * note at the top of this file).
 *
 * Buttons also skip the hover look while disabled and repeat their
 * pressed colour inside the same media block, because Tailwind emits
 * arbitrary-variant rules after every plain `active:` rule (so a plain
 * `active:` colour would lose to the hover colour on desktop):
 *   [@media(hover:hover)]:[&:not(:disabled)]:hover:bg-accent-600
 *   [@media(hover:hover)]:[&:not(:disabled)]:active:bg-accent-700
 * (`:not(:disabled)` rather than `enabled:`, which never matches the <a>
 * a link-button renders.)
 */
export const CAN_HOVER = "[@media(hover:hover)]";

/**
 * A word-sized action inside a line of text (Read, Undo, browse, Organise
 * for me), on a ghost Button: muted like the line, no box of its own (an
 * underline on pointing), and no more room around it than a word space.
 * A link that goes somewhere else keeps the accent colour; an action on
 * the page itself is this.
 */
export const INLINE_ACTION =
  "-mx-0.5 h-6 px-0.5 text-sm font-normal text-fg-muted underline-offset-2 [@media(hover:hover)]:[&:not(:disabled)]:hover:bg-transparent [@media(hover:hover)]:[&:not(:disabled)]:hover:underline";

/**
 * One stacking scale for everything that floats. Popovers (menus,
 * dropdown lists) sit above dialogs because they are opened from inside
 * them; a confirmation sits above everything.
 */
export const Z = {
  sticky: 40,
  dropdown: 50,
  drawer: 70,
  modal: 100,
  sheet: 110,
  toast: 120,
  popover: 130,
  confirm: 140,
} as const;

/**
 * Lift for menus, lists and dialogs: a soft shadow in the page's own
 * background colour (no colour literal), on top of their hairline border.
 */
export const OVERLAY_SHADOW =
  "shadow-[0_16px_40px_-12px_color-mix(in_srgb,var(--c-bg)_85%,transparent),0_2px_8px_color-mix(in_srgb,var(--c-bg)_45%,transparent)]";
